import React, { useState } from 'react';
import { TableRow } from './TableRow';
import { SecretCell } from './SecretCell';

export function TableBody({
    parentTable,
    processedData,
    allColumns,
    columns,
    pkCols,
    fks,
    reverseFks,
    fkOptions = {},
    isPolymorphicTable,
    polymorphicLabels,
    handleDeleteClick,
    handleCellSave,
    schemaWarning,
    tablesList = [],
    loadingPolyRecords = false,
    polymorphicRecords = {},
    distinctMode,
    isGrouped,
    SubgridComponent,
    activeSubgrids = [],
    globalSearchQuery,
    token,
    uiConfig,
    onMissingConfig,
    apiPrefix,
    draggingCol,
    dragOverCol,
    depth = 0,
    selectionMode,
    onSelectRecord,
    openAddModal,
    baseFilters = [],
    forceHideHeaders
}) {
    const [editingCell, setEditingCell] = useState(null); // { rowKey, colName }
    const [expandedGroups, setExpandedGroups] = useState({}); // { rowKey: true }
    const [expandedSubgrids, setExpandedSubgrids] = useState({}); // { 'rowKey_subgridKey': true }
    const [expandedTreeNodes, setExpandedTreeNodes] = useState({});

    const submitCellEdit = async (row, colName, newValue) => {
        if (handleCellSave) {
            await handleCellSave(row, colName, newValue);
            setEditingCell(null);
        }
    };

    const visibleColumns = columns || [];

    const toggleSubgrid = (rowKey, subgridKey) => {
        setExpandedSubgrids(prev => ({
            ...prev,
            [`${rowKey}_${subgridKey}`]: !prev[`${rowKey}_${subgridKey}`]
        }));
    };

    const toggleGroup = (rowKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [rowKey]: !prev[rowKey]
        }));
    };

    const generateRowKey = (row, index) => {
        if (pkCols && pkCols.length > 0) {
            const hasAllPks = pkCols.every(c => row[c.ColumnName] !== undefined);
            if (hasAllPks) return pkCols.map(c => row[c.ColumnName]).join('|');
        }
        // Hash all columns for a stable key (especially useful in distinctMode/grouping)
        const colsToHash = allColumns || columns || [];
        const str = colsToHash.map(c => row[c.ColumnName]).join('|');
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = ((hash << 5) - hash) + str.charCodeAt(i);
            hash |= 0;
        }
        return `row_${hash}_${index}`;
    };

    const renderRowAndChildren = (row, index, depth = 0) => {
        const rowKey = generateRowKey(row, index);
        row._treeDepth = depth;
        const hasChildren = row._isTreeNode && row._treeChildren && row._treeChildren.length > 0;
        const isExpanded = !!expandedTreeNodes[rowKey];

        const rowElement = (
            <React.Fragment key={`row-${rowKey}`}>
                <TableRow 
                    row={row}
                    index={index}
                    columns={visibleColumns}
                    pkCols={pkCols}
                    fks={fks}
                    fkOptions={fkOptions}
                    isPolymorphicTable={isPolymorphicTable}
                    polymorphicLabels={polymorphicLabels}
                    handleDeleteClick={handleDeleteClick}
                    schemaWarning={schemaWarning}
                    editingCell={editingCell}
                    setEditingCell={setEditingCell}
                    submitCellEdit={submitCellEdit}
                    draggingCol={draggingCol}
                    dragOverCol={dragOverCol}
                    isExpanded={isExpanded}
                    hasChildren={hasChildren || activeSubgrids.length > 0}
                    toggleExpand={() => {
                        setExpandedTreeNodes(prev => ({
                            ...prev,
                            [rowKey]: !prev[rowKey]
                        }));
                    }}
                    token={token}
                />
                {/* Render Subgrids (both FK and Self) */}
                {activeSubgrids && activeSubgrids.map(subgridKey => {
                    const isSelf = subgridKey.endsWith(':SELF');
                    const [subgridTable, fkCol] = subgridKey.split(':');
                    
                    let subgridFilters = [];
                    if (isSelf) {
                        // For self subgrid (grouping mode), filter only by visible columns that were actually returned (the grouped columns)
                        subgridFilters = visibleColumns
                            .filter(col => row[col.ColumnName] !== undefined)
                            .map(col => ({
                                column: col.ColumnName,
                                value: row[col.ColumnName] === null ? null : row[col.ColumnName]
                            }));
                    } else {
                        // For FK subgrids, filter by the matching primary key(s) of the parent row
                        // Find the reverse FK definition to map parent columns to target columns correctly
                        const revFk = reverseFks?.find(fk => fk.TableName === `${subgridTable}` && fk.ColumnName === fkCol);
                        
                        if (revFk) {
                            const parentCols = revFk.RefColumnName.split(',');
                            const targetCols = revFk.ColumnName.split(',');
                            
                            subgridFilters = [];
                            let isValid = true;
                            for (let i = 0; i < parentCols.length; i++) {
                                const pCol = parentCols[i].trim();
                                const tCol = targetCols[i].trim();
                                if (row[pCol] === undefined || row[pCol] === null) {
                                    isValid = false;
                                    break;
                                }
                                subgridFilters.push({ column: tCol, value: row[pCol] });
                            }
                            if (!isValid) return null;
                        } else {
                            // Fallback if not found (shouldn't happen)
                            const baseFilterKey = pkCols && pkCols.length > 0 ? pkCols[0].ColumnName : null; 
                            if (!baseFilterKey || row[baseFilterKey] === null) return null;
                            subgridFilters = [{ column: fkCol, value: row[baseFilterKey] }];
                        }
                    }

                    const countKey = `_count_${subgridKey}`;
                    const count = row[countKey] || 0;
                    
                    // Auto-expand if it's the ONLY active subgrid, and the main row is expanded!
                    const isOnlySubgrid = activeSubgrids.length === 1;
                    const isSubgridExpanded = isOnlySubgrid ? isExpanded : !!expandedSubgrids[`${rowKey}_${subgridKey}`];

                    // Determine breadcrumb description
                    let bcDesc = 'Row';
                    if (isSelf) {
                        bcDesc = visibleColumns.map(c => row[c.ColumnName]).join(' - ');
                    } else if (pkCols && pkCols.length > 0) {
                        bcDesc = pkCols.map(c => row[c.ColumnName]).join(' | ');
                    } else if (visibleColumns[0]) {
                        bcDesc = row[visibleColumns[0].ColumnName];
                    }

                    return (
                        <React.Fragment key={`sub-${rowKey}-${subgridKey}`}>
                            {/* Only show the subgrid toggler button if there are MULTIPLE subgrids */}
                            {!isOnlySubgrid && isExpanded && (
                                <tr className="bg-slate-800/40">
                                    <td colSpan={visibleColumns.length + 2} className="py-1 px-4 border-b border-slate-700/50">
                                        <button 
                                            onClick={() => toggleSubgrid(rowKey, subgridKey)}
                                            className={`flex items-center space-x-2 text-sm transition-colors ml-8 ${isSelf ? 'text-amber-300 hover:text-amber-200' : 'text-slate-300 hover:text-teal-400'}`}
                                        >
                                            <span className="material-symbols-outlined text-[18px]">
                                                {isSubgridExpanded ? 'remove_circle_outline' : 'add_circle_outline'}
                                            </span>
                                            <span>{isSelf ? `${parentTable} (Group Details)` : `${subgridTable} (${count})`}</span>
                                        </button>
                                    </td>
                                </tr>
                            )}
                            {isSubgridExpanded && isExpanded && (
                                <tr className="bg-slate-800/30">
                                    <td colSpan={visibleColumns.length + 2} className={`pt-0 pb-3 pr-4 border-b border-slate-700/50 ${!isOnlySubgrid ? 'pl-12' : 'pl-4'}`}>
                                        <div className={`py-1 ml-2 max-w-full overflow-x-auto ${!isOnlySubgrid ? 'pl-4 border-l border-slate-700/50' : 'pl-2'}`}>
                                            <SubgridComponent 
                                                targetTable={subgridTable}
                                                token={token}
                                                uiConfig={uiConfig}
                                                onMissingConfig={onMissingConfig}
                                                baseFilters={[...(baseFilters || []), ...subgridFilters]}
                                                globalSearchQuery={globalSearchQuery}
                                                apiPrefix={apiPrefix}
                                                viewKey={`subgrid_${parentTable}_${subgridKey}_d${depth}`}
                                                depth={depth + 1}
                                                forceHideHeaders={forceHideHeaders}
                                                pathContextData={{
                                                    table: parentTable,
                                                    description: bcDesc,
                                                    filters: subgridFilters
                                                }}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}

                {hasChildren && isExpanded && !isGrouped && (
                    row._treeChildren.map((child, childIdx) => renderRowAndChildren(child, `${index}_${childIdx}`, depth + 1))
                )}
            </React.Fragment>
        );
        return rowElement;
    };

    return (
        <tbody>
            {processedData.length === 0 ? (
                <tr>
                    <td colSpan={visibleColumns.length + 1} className="p-4 text-center text-slate-500">No records found.</td>
                </tr>
            ) : processedData.map((row, index) => {
                if (selectionMode) {
                    const rowKey = generateRowKey(row, index);
                    return (
                        <tr 
                            key={rowKey} 
                            className="border-b border-slate-700/50 hover:bg-teal-900/30 transition-colors cursor-pointer"
                            onClick={() => onSelectRecord(row)}
                        >
                            <td className="px-4 py-2 border-r border-slate-700/50">
                                <button className="text-teal-400 hover:text-teal-300 font-bold" title="Select Record">
                                    <span className="material-symbols-outlined text-[20px]">check_circle</span>
                                </button>
                            </td>
                            {columns.map(col => {
                                const colName = col.ColumnName;
                                let displayValue = row[colName];
                                if (isPolymorphicTable && colName === 'TargetRecordID') {
                                    const polyKey = `${row.TargetSchema}.${row.TargetTable}.${row.TargetRecordID}`;
                                    if (polymorphicLabels[polyKey]) {
                                        displayValue = polymorphicLabels[polyKey];
                                    }
                                }
                                return (
                                    <td key={colName} className="px-4 py-2 group/cell align-top">
                                        <div className="max-w-xs overflow-hidden text-ellipsis whitespace-nowrap group-hover/cell:whitespace-normal group-hover/cell:bg-slate-800 group-hover/cell:z-10 group-hover/cell:relative group-hover/cell:rounded group-hover/cell:shadow-lg transition-all" title={displayValue}>
                                            {displayValue === null || displayValue === undefined ? <span className="text-slate-500 italic">null</span> : String(displayValue)}
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    );
                }
                return renderRowAndChildren(row, index, 0);
            })}
        </tbody>
    );
}
